import { ScheduleRule as ScheduleRuleModel } from 'shared/models';
import * as moment from 'moment-timezone';

export type Option = 'hasFromToDate' | 'hasFromToTime' | 'hasDaysOfWeek';

export class ScheduleRule {
    themeId: string;
    hasFromToDate: boolean;
    hasFromToTime: boolean;
    hasDaysOfWeek: boolean;
    day0?: boolean;
    day1?: boolean;
    day2?: boolean;
    day3?: boolean;
    day4?: boolean;
    day5?: boolean;
    day6?: boolean;
    fromDate?: number;
    toDate?: number;
    fromTime?: number; // Minutes from midnight
    toTime?: number; // Minutes from midnight

    constructor(clone: ScheduleRule | null = null, delta: any = null) {
        this.themeId = '';
        this.hasFromToDate = false;
        this.hasFromToTime = false;
        this.hasDaysOfWeek = false;
        Object.assign(this, clone, delta);
    }

    static fromModel(rule: ScheduleRuleModel): ScheduleRule {
        return new ScheduleRule(null, rule);
    }

    withTheme(themeId: string): ScheduleRule {
        return new ScheduleRule(this, { themeId });
    }

    withOption(option: Option, value: boolean): ScheduleRule {
        return new ScheduleRule(this, { [option]: value });
    }

    withDay(day: number, value: boolean): ScheduleRule {
        return new ScheduleRule(this, { [`day${day}`]: value });
    }

    withFromDate(fromDate: number): ScheduleRule {
        return new ScheduleRule(this, { fromDate });
    }

    withToDate(toDate: number): ScheduleRule {
        return new ScheduleRule(this, { toDate });
    }

    withFromTime(fromTime: number): ScheduleRule {
        return new ScheduleRule(this, { fromTime });
    }

    withToTime(toTime: number): ScheduleRule {
        return new ScheduleRule(this, { toTime });
    }

    toString() {
        return JSON.stringify(this);
    }
}

const numberToDate = (input: number, timeZone: string) => {
    if (typeof input === 'number') {
        try {
            return moment.tz(input, timeZone).format('YYYY-MM-DD');
        }
        catch { /* swallow error */ }
    }
};

const dateToNumber = (input: string, timeZone: string) => {
    try {
        return moment.tz(input, timeZone).valueOf();
    }
    catch { /* swallow error */ }
};

const timeToNumber = (input: string) => {
    if (typeof input === 'string' && input.length) {
        try {
            return moment.utc(`2018-01-01 ${input}`, 'YYYY-MM-DD hh:mm')
                .diff(moment.utc('2018-01-01', 'YYYY-MM-DD'), 'minutes');
        }
        catch { /* swallow error */ }
    }
};

export const numberToTime = (input: number) => {
    if (typeof input === 'number') {
        try {
            return moment.utc('2018-01-01', 'YYYY-MM-DD')
                .add(input, 'minutes')
                .format('HH:mm');
        }
        catch { /* swallow error */ }
    }
};

export const humanizeScheduleRule = (rule: ScheduleRuleModel, timeZone: string) => {
    // Patch up the schedule rule dates and times to be strings (for easier consumption by redux-form)
    if (rule.hasFromToTime) {
        if (typeof rule.fromTime === 'number') {
            rule.fromTime = numberToTime(rule.fromTime) as any;
        }
        if (typeof rule.toTime === 'number') {
            rule.toTime = numberToTime(rule.toTime) as any;
        }
    }
    if (rule.hasFromToDate) {
        if (typeof rule.fromDate === 'number') {
            rule.fromDate = numberToDate(rule.fromDate, timeZone) as any;
        }
        if (typeof rule.toDate === 'number') {
            rule.toDate = numberToDate(rule.toDate, timeZone) as any;
        }
    }
    return rule;
};

export const dehumanizeScheduleRule = (rule: ScheduleRule, timeZone: string) => {
    // Convert the string times back into numbers (number of minutes)
    const { hasFromToTime, fromTime, toTime, hasFromToDate, fromDate, toDate } = rule;

    const r = { ...rule };

    if (hasFromToTime) {
        r.fromTime = timeToNumber(fromTime as any);
        r.toTime = timeToNumber(toTime as any);
    }

    if (hasFromToDate) {
        r.fromDate = dateToNumber(fromDate as any, timeZone);
        r.toDate = dateToNumber(toDate as any, timeZone);
    }

    return r;
};